import { Analytics } from '@cobliteam/analytics';
import { Location } from '@cobliteam/common-types';
import useChatWidget from '@cobliteam/hooks/src/useChatWidget';
import { computePositionRect, POSITION_TRANSITION, useChatWidgetPositionStore } from '@cobliteam/hooks/src/useChatWidget/store';
import useFeatureFlags from '@cobliteam/hooks/src/useFeatureFlags';
import { useTranslation } from '@cobliteam/i18n';
import { lazy } from '@cobliteam/lazy-with-retries';
import Button from '@cobliteam/ui/components/Button';
import Popover, { PopoverContent, PopoverTrigger } from '@cobliteam/ui/components/Popover';
import Icon from '@cobliteam/ui/icons';
import { Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { COBLI_ACTION_EVENT, type CobliActionEventDetail } from '../../lib/cobliLinkActions';
import { useAIAssistant, useFloatingAIChat, useFloatingChat } from '../../store';

// The floating chat panel owns the full AI chat stack (Mastra client, streamdown,
// shiki, chart tools, react-markdown). AIChatFloatingButton itself mounts on every
// authenticated page via AppLayout, but the panel only appears when the user actually
// opens the mini-chat. Lazy so ~600 KB of chat infrastructure isn't in the entry.
const AIChatFloatingPanel = lazy(() => import('./components/AIChatFloatingPanel'));

const AIChatFloatingButton = () => {
  const { t } = useTranslation('ai');
  const { openIntercom, closeIntercom, hide, isFooterVisible } = useChatWidget();
  const position = useChatWidgetPositionStore((s) => s.position);
  const { isAISidepanelOpen, openAISidepanel } = useAIAssistant();
  const { aiChat, aiRecommendations2 } = useFeatureFlags();
  const showAiChatOption = aiChat || aiRecommendations2;
  const aiChatThreadIdRef = useRef(crypto.randomUUID());
  const { intercomOpen, setIntercomOpen } = useFloatingChat();
  const [popoverOpen, setPopoverOpen] = useState(false);

  const { isOpen: aiChatOpen, initialMessage, close: closeAIChat } = useFloatingAIChat();

  const { pathname } = useLocation();
  const isAIPage = pathname === Location.AI || pathname.startsWith(`${Location.AI}/`);

  const positionStyle = useMemo(() => {
    const { right, bottom } = computePositionRect(position, isFooterVisible);
    return { right: `${right}px`, bottom: `${bottom}px`, transition: POSITION_TRANSITION };
  }, [position, isFooterVisible]);

  useEffect(() => {
    hide(true);
    return () => hide(false);
  }, [hide]);

  // When the AI agent emits a `#cobli-open-blip-chat` link, AIChatMarkdown
  // opens Blip and dispatches this event so the floating AI chat panel closes
  // itself in the same gesture (the user clicks once; the chat panel goes
  // away and the Blip window takes over).
  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<CobliActionEventDetail>).detail;
      if (detail?.action === 'open-blip-chat' && aiChatOpen) {
        Analytics.track('AI - Floating Chat - Closed', { component: 'floating-chat-button', trigger: 'ai-link-action' });
        closeAIChat();
      }
    };
    window.addEventListener(COBLI_ACTION_EVENT, handler);
    return () => window.removeEventListener(COBLI_ACTION_EVENT, handler);
  }, [aiChatOpen, closeAIChat]);

  const isAnyChatOpen = aiChatOpen || intercomOpen;

  const handleCloseAIChat = (trigger: 'fab-toggle' | 'panel-close-button') => {
    if (!aiChatOpen) return;
    Analytics.track('AI - Floating Chat - Closed', { component: 'floating-chat-button', trigger });
    closeAIChat();
  };

  const closeAllChats = () => {
    if (intercomOpen) {
      closeIntercom();
      setIntercomOpen(false);
    }
    handleCloseAIChat('fab-toggle');
  };

  const handleButtonClick = () => {
    if (isAnyChatOpen) {
      closeAllChats();
    }
  };

  const handleIntercomChat = () => {
    openIntercom();
    setIntercomOpen(true);
    setPopoverOpen(false);
  };

  const handleAIChatClick = () => {
    Analytics.track('AI - Floating Chat - Opened', { component: 'floating-chat-button' });
    openAISidepanel();
    setPopoverOpen(false);
  };

  // Mirror AISidepanelStateSync's behavior on the chat widget: when the
  // AI sidepanel takes over the right side, the floating chat (and the agent
  // panel inside it) hide so they don't overlap. State is preserved via zustand
  // — when the sidepanel closes, this remounts and any open chat is restored.
  // The full-page AI chat (/ai and its subpages) already exposes the assistant,
  // so the floating button would be redundant there.
  if (isAISidepanelOpen || isAIPage) return null;

  return (
    <>
      <div data-testid='floating-chat-button' className='fixed z-1001' style={positionStyle}>
        <Popover open={isAnyChatOpen ? false : popoverOpen} onOpenChange={isAnyChatOpen ? undefined : setPopoverOpen}>
          <PopoverTrigger asChild>
            <Button
              variant='contained'
              color='primary'
              icon={<Icon name={isAnyChatOpen ? 'x' : 'bot'} size='md' />}
              className='mr-2 h-14 w-14 rounded-full shadow-lg'
              onClick={handleButtonClick}
              aria-label={isAnyChatOpen ? t('floating_chat.close_aria_label') : t('floating_chat.open_aria_label')}
            />
          </PopoverTrigger>
          <PopoverContent align='end' className='mb-2 w-56 p-1'>
            <button
              data-testid='intercom-option'
              type='button'
              className='flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm hover:bg-gray-2'
              onClick={handleIntercomChat}
            >
              <Icon name='headset' size='sm' className='text-gray-9' />
              <span>{t('floating_chat.cobli_support')}</span>
            </button>
            {showAiChatOption && (
              <button
                data-testid='ai-chat-option'
                type='button'
                className='flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm hover:bg-gray-2'
                onClick={handleAIChatClick}
              >
                <Icon name='sparkles' size='sm' className='text-primary-9' />
                <span>{t('floating_chat.ai_chat')}</span>
              </button>
            )}
          </PopoverContent>
        </Popover>
      </div>

      {aiChatOpen && (
        <Suspense fallback={null}>
          <AIChatFloatingPanel onClose={() => handleCloseAIChat('panel-close-button')} initialMessage={initialMessage} threadId={aiChatThreadIdRef.current} />
        </Suspense>
      )}
    </>
  );
};

export default AIChatFloatingButton;
